import React, { useCallback, useEffect, useMemo, useState } from "react";
import { buildApiUrl, getApiHeaders } from "./config";
import { getAuthToken } from "./auth/authUtils";
import "./AdminMonitoringDashboard.css";
import "./AdminMonitoringSorting.css";

const headers = () => getApiHeaders({ Authorization: `Bearer ${getAuthToken()}` });
const value = (object, key) => object?.[key] ?? object?.[key.toUpperCase()] ?? "—";
const rawValue = (object, key) => object?.[key] ?? object?.[key.toUpperCase()] ?? null;
const size = (mb) => Number(mb) >= 1024 ? `${(Number(mb) / 1024).toFixed(2)} GB` : `${Number(mb || 0).toFixed(2)} MB`;

function compareRows(left, right, key, type) {
  const leftValue = rawValue(left, key);
  const rightValue = rawValue(right, key);
  if (leftValue == null && rightValue == null) return 0;
  if (leftValue == null) return 1;
  if (rightValue == null) return -1;
  if (type === "number") return Number(leftValue) - Number(rightValue);
  if (type === "date") return new Date(leftValue).getTime() - new Date(rightValue).getTime();
  return String(leftValue).localeCompare(String(rightValue), undefined, { numeric: true, sensitivity: "base" });
}

function sortedRows(rows, sort) {
  return [...rows].sort((left, right) => {
    const comparison = compareRows(left, right, sort.key, sort.type);
    return sort.direction === "asc" ? comparison : -comparison;
  });
}

function SortableHeader({ label, columnKey, type = "text", sort, onSort }) {
  const active = sort.key === columnKey;
  const direction = active ? sort.direction : "none";
  return <th aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>
    <button className={`monitor-sort ${active ? "monitor-sort--active" : ""}`} type="button"
      onClick={() => onSort(columnKey, type)}>
      <span>{label}</span><span className="monitor-sort__icon" aria-hidden="true">
        {direction === "asc" ? "▲" : direction === "desc" ? "▼" : "↕"}
      </span>
    </button>
  </th>;
}

function MetricCard({ title, current, detail, crossed, status }) {
  const danger = crossed || status === "DOWN" || status === "DEGRADED";
  return <article className={`monitor-card ${danger ? "monitor-card--danger" : "monitor-card--healthy"}`}>
    <span>{title}</span>
    <strong>{current ?? "—"}</strong>
    <small>{detail}</small>
  </article>;
}

export default function AdminMonitoringDashboard() {
  const [overview, setOverview] = useState(null);
  const [tables, setTables] = useState([]);
  const [queries, setQueries] = useState({ available: true, queries: [] });
  const [lifecycle, setLifecycle] = useState([]);
  const [retention, setRetention] = useState({ running: false, policies: [] });
  const [retentionRuns, setRetentionRuns] = useState([]);
  const [retentionBusy, setRetentionBusy] = useState("");
  const [retentionMessage, setRetentionMessage] = useState("");
  const [tableSort, setTableSort] = useState({ key: "totalMb", type: "number", direction: "desc" });
  const [querySort, setQuerySort] = useState({ key: "totalSeconds", type: "number", direction: "desc" });
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const [overviewResponse, tableResponse, queryResponse, lifecycleResponse, retentionResponse, retentionRunsResponse] = await Promise.all([
        fetch(buildApiUrl("/api/bot/ui/admin/monitoring/overview"), { headers: headers(), cache: "no-store" }),
        fetch(buildApiUrl("/api/bot/ui/admin/monitoring/tables", { limit: 150 }), { headers: headers(), cache: "no-store" }),
        fetch(buildApiUrl("/api/bot/ui/admin/monitoring/queries", { limit: 50 }), { headers: headers(), cache: "no-store" }),
        fetch(buildApiUrl("/api/bot/ui/admin/monitoring/lifecycle", { limit: 25 }), { headers: headers(), cache: "no-store" }),
        fetch(buildApiUrl("/api/bot/ui/admin/retention/policies"), { headers: headers(), cache: "no-store" }),
        fetch(buildApiUrl("/api/bot/ui/admin/retention/runs", { limit: 100 }), { headers: headers(), cache: "no-store" }),
      ]);
      if ([overviewResponse, tableResponse, queryResponse, lifecycleResponse, retentionResponse, retentionRunsResponse].some(response => !response.ok)) {
        throw new Error("Monitoring API rejected the request. Confirm the login has ROLE_ADMIN.");
      }
      setOverview(await overviewResponse.json());
      setTables(await tableResponse.json());
      setQueries(await queryResponse.json());
      setLifecycle(await lifecycleResponse.json());
      setRetention(await retentionResponse.json());
      setRetentionRuns(await retentionRunsResponse.json());
    } catch (requestError) {
      setError(requestError.message || "Unable to load monitoring metrics.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = window.setInterval(load, 60000);
    return () => window.clearInterval(id);
  }, [autoRefresh, load]);

  const app = overview?.application || {};
  const server = overview?.server || {};
  const db = overview?.database || {};
  const rds = overview?.rds || {};
  const visibleTables = useMemo(() => sortedRows(tables, tableSort), [tables, tableSort]);
  const visibleQueries = useMemo(() => sortedRows(queries.queries || [], querySort), [queries, querySort]);
  const changeSort = (setter) => (key, type) => setter(current => ({
    key,
    type,
    direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
  }));

  const retentionAction = async (policy, action, payload) => {
    const busyKey = `${policy.id}-${action}`;
    setRetentionBusy(busyKey);
    setRetentionMessage("");
    try {
      const response = await fetch(buildApiUrl(`/api/bot/ui/admin/retention/policies/${policy.id}/${action}`), {
        method: "POST",
        headers: headers(),
        body: payload ? JSON.stringify(payload) : undefined,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || result.error || `Retention ${action} failed`);
      if (action === "preview") {
        setRetentionMessage(`${policy.tableName}: ${Number(result.eligibleRows || 0).toLocaleString()} eligible rows, approximately ${size(result.estimatedReclaimableMb)} reclaimable.`);
      } else {
        setRetentionMessage(`${policy.tableName}: ${result.status || (result.paused ? "paused" : "enabled")}. ${Number(result.deletedRows || 0).toLocaleString()} rows deleted.`);
      }
      await load();
    } catch (actionError) {
      setRetentionMessage(actionError.message || "Retention action failed.");
    } finally {
      setRetentionBusy("");
    }
  };

  const runLive = (policy) => {
    const confirmation = window.prompt(
      `This permanently deletes eligible records from ${policy.tableName}.\nType DELETE to continue.`
    );
    if (confirmation !== "DELETE") return;
    retentionAction(policy, "run", { dryRun: false, confirmation });
  };

  return <section className="admin-section admin-monitoring">
    <div className="monitor-heading">
      <div><span className="admin-dashboard-eyebrow">Operations</span><h2>System & Database Monitoring</h2>
        <p>Admin-only live resource, capacity, and query diagnostics.</p></div>
      <div className="monitor-actions">
        <label><input type="checkbox" checked={autoRefresh} onChange={event => setAutoRefresh(event.target.checked)} /> Auto-refresh</label>
        <button type="button" onClick={load} disabled={loading}>{loading ? "Refreshing…" : "Refresh now"}</button>
      </div>
    </div>
    {error && <div className="monitor-error">{error}</div>}
    <div className="monitor-tabs">
      {["overview", "tables", "queries", "retention"].map(item =>
        <button type="button" key={item} className={tab === item ? "active" : ""} onClick={() => setTab(item)}>
          {item === "overview" ? "Health & Resources" : item === "tables" ? "Table Storage" :
            item === "queries" ? "Major Queries" : "Retention & Cleanup"}
        </button>)}
    </div>

    {tab === "overview" && <div>
      <div className="monitor-grid">
        <MetricCard title="Application health" current={app.status} status={app.status}
          detail={`Uptime ${value(app, "uptimeSeconds")} seconds`} />
        <MetricCard title="Server CPU" current={`${value(server, "cpuPercent")}%`}
          crossed={server.cpuThresholdCrossed} detail={`Threshold ${value(server, "cpuThresholdPercent")}%`} />
        <MetricCard title="Server memory" current={`${value(server, "memoryUsedPercent")}%`}
          crossed={server.memoryThresholdCrossed}
          detail={`${value(server, "memoryFreeGb")} GB free of ${value(server, "memoryTotalGb")} GB`} />
        <MetricCard title="JVM heap" current={`${value(app, "heapUsedPercent")}%`}
          crossed={app.heapThresholdCrossed}
          detail={`${value(app, "heapUsedMb")} MB / ${value(app, "heapMaxMb")} MB`} />
        <MetricCard title="Database health" current={db.status} status={db.status}
          detail={`${value(db, "databaseName")} · MySQL ${value(db, "version")}`} />
        <MetricCard title="RDS CPU" current={rds.available ? `${value(rds, "cpuPercent")}%` : "Unavailable"}
          crossed={rds.cpuThresholdCrossed} detail={`Threshold ${value(rds, "cpuThresholdPercent")}%`} />
        <MetricCard title="RDS free memory" current={rds.available ? `${value(rds, "freeMemoryGb")} GB` : "Unavailable"}
          crossed={rds.memoryThresholdCrossed} detail={`Minimum ${value(rds, "freeMemoryThresholdGb")} GB`} />
        <MetricCard title="DB connections" current={value(db, "Threads_connected")}
          detail={`${value(db, "Threads_running")} running · pool ${value(db, "poolActive")}/${value(db, "poolMaximum")}`} />
      </div>
      {!rds.available && rds.message && <p className="monitor-note">{rds.message}</p>}
      <div className="monitor-details">
        <h3>Database activity</h3>
        <dl>
          <dt>Queries since startup</dt><dd>{value(db, "Questions")}</dd>
          <dt>Slow queries</dt><dd>{value(db, "Slow_queries")}</dd>
          <dt>Connections opened</dt><dd>{value(db, "Connections")}</dd>
          <dt>Aborted connections</dt><dd>{value(db, "Aborted_connects")}</dd>
          <dt>Hikari waiting threads</dt><dd>{value(db, "poolWaiting")}</dd>
          <dt>RDS instance</dt><dd>{value(rds, "instanceIdentifier")}</dd>
        </dl>
      </div>
      <div className="monitor-details">
        <h3>Webhook startup and shutdown history</h3>
        <div className="monitor-table-wrap monitor-table-wrap--compact"><table><thead><tr>
          <th>Started</th><th>Stopped</th><th>Status</th><th>Host / PID</th><th>Reason</th>
        </tr></thead><tbody>{lifecycle.map((row, index) => <tr key={index}>
          <td>{value(row, "startedAt")}</td><td>{value(row, "stoppedAt")}</td>
          <td><strong>{value(row, "status")}</strong></td>
          <td>{value(row, "hostName")} / {value(row, "processId")}</td>
          <td>{value(row, "shutdownReason")}</td>
        </tr>)}</tbody></table></div>
        {lifecycle.length === 0 && <p className="monitor-note">No lifecycle records are available yet. They appear after the updated webhook service starts.</p>}
      </div>
    </div>}

    {tab === "tables" && <div className="monitor-table-wrap"><table><thead><tr>
      <SortableHeader label="Table" columnKey="tableName" sort={tableSort} onSort={changeSort(setTableSort)} />
      <SortableHeader label="Estimated rows" columnKey="estimatedRows" type="number" sort={tableSort} onSort={changeSort(setTableSort)} />
      <SortableHeader label="Data" columnKey="dataMb" type="number" sort={tableSort} onSort={changeSort(setTableSort)} />
      <SortableHeader label="Indexes" columnKey="indexMb" type="number" sort={tableSort} onSort={changeSort(setTableSort)} />
      <SortableHeader label="Total" columnKey="totalMb" type="number" sort={tableSort} onSort={changeSort(setTableSort)} />
      <SortableHeader label="Engine" columnKey="engine" sort={tableSort} onSort={changeSort(setTableSort)} />
      <SortableHeader label="Last update" columnKey="updatedAt" type="date" sort={tableSort} onSort={changeSort(setTableSort)} />
    </tr></thead><tbody>{visibleTables.map((row, index) => <tr key={`${value(row, "tableName")}-${index}`}>
      <td>{value(row, "tableName")}</td><td>{Number(value(row, "estimatedRows") || 0).toLocaleString()}</td>
      <td>{size(value(row, "dataMb"))}</td><td>{size(value(row, "indexMb"))}</td>
      <td><strong>{size(value(row, "totalMb"))}</strong></td><td>{value(row, "engine")}</td>
      <td>{value(row, "updatedAt")}</td></tr>)}</tbody></table></div>}

    {tab === "queries" && <div>
      {!queries.available && <div className="monitor-error">{queries.message}</div>}
      <div className="monitor-table-wrap"><table><thead><tr>
        <SortableHeader label="Query digest" columnKey="queryText" sort={querySort} onSort={changeSort(setQuerySort)} />
        <SortableHeader label="Executions" columnKey="executionCount" type="number" sort={querySort} onSort={changeSort(setQuerySort)} />
        <SortableHeader label="Total time" columnKey="totalSeconds" type="number" sort={querySort} onSort={changeSort(setQuerySort)} />
        <SortableHeader label="Average" columnKey="averageMs" type="number" sort={querySort} onSort={changeSort(setQuerySort)} />
        <SortableHeader label="Rows examined" columnKey="rowsExamined" type="number" sort={querySort} onSort={changeSort(setQuerySort)} />
        <SortableHeader label="Rows sent" columnKey="rowsSent" type="number" sort={querySort} onSort={changeSort(setQuerySort)} />
      </tr></thead><tbody>{visibleQueries.map((row, index) => <tr key={index}>
        <td className="monitor-query">{value(row, "queryText")}</td>
        <td>{Number(value(row, "executionCount") || 0).toLocaleString()}</td>
        <td>{value(row, "totalSeconds")} s</td><td>{value(row, "averageMs")} ms</td>
        <td>{Number(value(row, "rowsExamined") || 0).toLocaleString()}</td>
        <td>{Number(value(row, "rowsSent") || 0).toLocaleString()}</td>
      </tr>)}</tbody></table></div>
    </div>}

    {tab === "retention" && <div className="retention-panel">
      <div className="retention-summary">
        <MetricCard title="Retention engine" current={retention.running ? "RUNNING" : "READY"}
          crossed={retention.running} detail={`${retention.policies?.length || 0} configured policies`} />
        <MetricCard title="Live deletion" current={retention.policies?.some(item => item.liveDeletionAllowed) ? "ALLOWED" : "LOCKED"}
          status={retention.policies?.some(item => item.liveDeletionAllowed) ? "UP" : "DEGRADED"}
          detail="Requires server environment enablement" />
        <MetricCard title="Default mode" current={retention.policies?.some(item => item.configuredDryRun) ? "DRY RUN" : "LIVE"}
          detail="Every execution is audited" />
        <MetricCard title="Failed runs" current={retentionRuns.filter(item => value(item, "status") === "FAILED").length}
          crossed={retentionRuns.some(item => value(item, "status") === "FAILED")}
          detail={`${retentionRuns.length} recent executions`} />
      </div>
      <p className="monitor-note">
        Deletion is permanent. Preview first. Live runs are bounded by batch, row, and runtime limits and cannot overlap.
      </p>
      {retentionMessage && <div className="retention-message">{retentionMessage}</div>}
      <div className="monitor-details retention-archive-flows">
        <h3>Current-to-history archival</h3>
        <div className="monitor-table-wrap monitor-table-wrap--compact"><table><thead><tr>
          <th>Flow</th><th>Source</th><th>History table</th><th>Rule</th><th>Last run</th><th>Archived</th><th>Status</th>
        </tr></thead><tbody>{(retention.archiveFlows || []).map(flow => {
          const latest = flow.latestRun || {};
          return <tr key={flow.id}>
            <td><strong>{flow.id}</strong></td><td>{flow.sourceTable}</td><td>{flow.historyTable}</td>
            <td>{flow.description}</td><td>{value(latest, "startedAt")}</td>
            <td>{Number(rawValue(latest, "deletedRows") || 0).toLocaleString()}</td>
            <td>{value(latest, "status")}</td>
          </tr>;
        })}</tbody></table></div>
      </div>
      <div className="monitor-table-wrap retention-policy-table"><table><thead><tr>
        <th>Policy / table</th><th>Retention</th><th>Batch</th><th>Purpose</th><th>Last run</th><th>Status</th><th>Controls</th>
      </tr></thead><tbody>{(retention.policies || []).map(policy => {
        const latest = policy.latestRun || {};
        return <tr key={policy.id}>
          <td><strong>{policy.id}</strong><small>{policy.tableName}</small></td>
          <td>{policy.retentionDays === 0 ? "At expiry" : `${policy.retentionDays} days`}
            {policy.backtestingData && <span className="retention-tag">Backtesting</span>}</td>
          <td>{Number(policy.batchSize || 0).toLocaleString()}</td>
          <td className="retention-description">{policy.description}</td>
          <td>{value(latest, "startedAt")}<small>{Number(rawValue(latest, "deletedRows") || 0).toLocaleString()} deleted</small></td>
          <td><span className={`retention-status retention-status--${String(value(latest, "status")).toLowerCase()}`}>
            {policy.paused ? "PAUSED" : value(latest, "status")}</span></td>
          <td><div className="retention-controls">
            <button type="button" disabled={!!retentionBusy} onClick={() => retentionAction(policy, "preview")}>
              {retentionBusy === `${policy.id}-preview` ? "Checking..." : "Preview"}
            </button>
            <button type="button" disabled={!!retentionBusy} onClick={() => retentionAction(policy, "run", { dryRun: true, confirmation: "" })}>
              Dry run
            </button>
            <button className="retention-delete" type="button"
              disabled={!!retentionBusy || !policy.liveDeletionAllowed || policy.configuredDryRun} onClick={() => runLive(policy)}>
              Delete eligible
            </button>
            <button type="button" disabled={!!retentionBusy}
              onClick={() => retentionAction(policy, "pause", { paused: !policy.paused })}>
              {policy.paused ? "Enable" : "Pause"}
            </button>
          </div></td>
        </tr>;
      })}</tbody></table></div>

      <div className="monitor-details">
        <h3>Retention execution history</h3>
        <div className="monitor-table-wrap monitor-table-wrap--compact"><table><thead><tr>
          <th>Started</th><th>Policy</th><th>Mode</th><th>Status</th><th>Eligible</th><th>Deleted</th><th>Duration</th><th>Triggered by</th><th>Error</th>
        </tr></thead><tbody>{retentionRuns.map(run => <tr key={value(run, "id")}>
          <td>{value(run, "startedAt")}</td><td>{value(run, "policyId")}</td><td>{value(run, "mode")}</td>
          <td><strong>{value(run, "status")}</strong></td>
          <td>{Number(rawValue(run, "eligibleRows") || 0).toLocaleString()}</td>
          <td>{Number(rawValue(run, "deletedRows") || 0).toLocaleString()}</td>
          <td>{Number(rawValue(run, "durationMs") || 0).toLocaleString()} ms</td>
          <td>{value(run, "triggeredBy")}</td><td className="retention-error-cell">{value(run, "errorMessage")}</td>
        </tr>)}</tbody></table></div>
      </div>
    </div>}
    {overview?.generatedAt && <p className="monitor-updated">Generated: {new Date(overview.generatedAt).toLocaleString()}</p>}
  </section>;
}
